/**
 * Перевод кодов форматов из `knowledge/formats/` на русский — ОДИН словарь на весь пакет.
 *
 * ДВА СПИСКА, И У НИХ РАЗНАЯ РАБОТА (релиз 2026-10-14, docs/release-2026-10-14-news-digest.md).
 *   • Что человек МОЖЕТ заказать. С 2026-11-02 — список БРЕНДА: бэкенд отдаёт его полем
 *     `selectable_formats` в `GET /api/v1/brands` (форматы, подключённые базой знаний бренда, в
 *     порядке библиотеки), а `selectableFormatsOf` ниже оставляет из него только то, что диалог
 *     умеет заказать (`ORDERABLE_FORMATS`). Нет поля (бэкенд старше) — прежний общий список
 *     `SELECTABLE_FORMATS`: новость и дайджест недели. Обзор конференции заказывает только бренд
 *     со списком наблюдения (AGI Boardroom), и общим этот список стать не может: GeoBoost получил
 *     бы кнопку, которую бэкенд отбивает. Чипы по-прежнему НЕ выводятся из `known_formats`
 *     (истории бренда): история помнит снятые коды и возвращала бы их в выбор вечно.
 *   • `FORMAT_LABEL` — как ПОКАЗАТЬ код, который уже лежит в данных. Снятые коды остаются
 *     здесь с пометкой «(снят)»: старые черновики, темы и заявки несут их, и человек должен
 *     прочитать их словом, а не кодом. Удалять их отсюда нельзя, пока такие записи живы.
 *
 * ПОЧЕМУ ОТДЕЛЬНЫЙ МОДУЛЬ, А НЕ КОНСТАНТА В ДИАЛОГЕ. Словарь нужен диалогу генерации, чипу
 * панели темы (`TopicPanel`) и таблице оценок на вкладке «Мониторинг». Второй копии заводить
 * нельзя — она разъедется на первом новом формате, — поэтому дом один и здесь.
 */

import { EVENT_REVIEW_FORMAT } from '@smm/contracts/schemas';

/** Новость — умолчание диалога и единственный формат одиночного поста. */
export const NEWS_FORMAT = 'news';

/** Дайджест недели: 3–5 поводов за последние 5 дней, собирает мониторинг. */
export const WEEKLY_DIGEST_FORMAT = 'weekly-digest';

/**
 * Обзор конференции (AGI Boardroom, 2026-11-02): главные анонсы прошедшей конференции из списка
 * наблюдения бренда и что они меняют. Код — константа контрактов: по ней же бэкенд ищет выпуск
 * в `event_watch`, а писатель выбирает режим промпта.
 */
export { EVENT_REVIEW_FORMAT };

/**
 * Прежний общий выбор — запас на ответ бэкенда без `selectable_formats`. Порядок — порядок
 * чипов в диалоге: умолчание первым.
 */
export const SELECTABLE_FORMATS = [NEWS_FORMAT, WEEKLY_DIGEST_FORMAT] as const;
export type SelectableFormat = (typeof SELECTABLE_FORMATS)[number];

/**
 * Всё, что диалог «Запустить генерацию» УМЕЕТ заказать: у каждого кода здесь своя ветка формы
 * (ссылка, контекст, обещание сроков). Код из списка бренда, которого здесь нет, кнопки не
 * получает: без своей ветки он ушёл бы на сервер формой новости. Новый генерируемый формат — это
 * строка здесь, подпись ниже и ветка диалога, а не только файл библиотеки.
 */
export const ORDERABLE_FORMATS = [NEWS_FORMAT, WEEKLY_DIGEST_FORMAT, EVENT_REVIEW_FORMAT] as const;
export type OrderableFormat = (typeof ORDERABLE_FORMATS)[number];

const isOrderable = (v: unknown): v is OrderableFormat =>
  typeof v === 'string' && (ORDERABLE_FORMATS as readonly string[]).includes(v);

/**
 * Форматы бренда из ответа `GET /api/v1/brands` (`selectable_formats`) — по полю, а не схемой:
 * список брендов читается снисходительно (`brand.tsx`). Порядок — бэкенда (библиотека бренда),
 * только новость, если она есть, встаёт первой: она умолчание диалога. Повторы и коды, которых
 * диалог не умеет заказать, выпадают.
 *
 * Не список (поля нет, чужой ответ) — прежний общий список. Список без единого заказываемого
 * кода — тоже: у каждого бренда с писателем новость в библиотеке есть, и пустой ряд чипов значил
 * бы сломанный ответ, а не «заказывать нечего». Замок всё равно у бэкенда: формат вне базы
 * знаний бренда он отбивает 422.
 */
export function selectableFormatsOf(raw: unknown): readonly OrderableFormat[] {
  if (!Array.isArray(raw)) return SELECTABLE_FORMATS;
  const out: OrderableFormat[] = [];
  for (const code of raw as readonly unknown[]) {
    if (isOrderable(code) && !out.includes(code)) out.push(code);
  }
  if (out.length === 0) return SELECTABLE_FORMATS;
  return out.includes(NEWS_FORMAT) ? [NEWS_FORMAT, ...out.filter((f) => f !== NEWS_FORMAT)] : out;
}

export const FORMAT_LABEL: Readonly<Record<string, string>> = {
  [NEWS_FORMAT]: 'Новость',
  [WEEKLY_DIGEST_FORMAT]: 'Дайджест недели',
  [EVENT_REVIEW_FORMAT]: 'Обзор конференции',
  // Сняты релизом 2026-10-14 — только для показа старых записей.
  opinion: 'Мнение (снят)',
  digest: 'Дайджест (снят)',
  guide: 'Инструкция (снят)',
  'mini-research': 'Мини-исследование (снят)',
};

/** Незнакомый код — не ошибка: показывается как есть, а не прячется. */
export const formatLabel = (code: string): string => FORMAT_LABEL[code] ?? code;

/**
 * Форматы словами через запятую и «или» — «„Новость“ или „Обзор конференции“»: подсказка о
 * кнопках, которые стоят рядом, называет ровно их, а не прежнюю пару.
 */
export function formatChoiceText(codes: readonly string[]): string {
  const words = codes.map((c) => `«${formatLabel(c)}»`);
  if (words.length <= 1) return words.join('');
  return `${words.slice(0, -1).join(', ')} или ${words.at(-1) ?? ''}`;
}
